import React, { useState, useEffect } from 'react';
import { Form, Spinner, Badge } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { MdLocalShipping } from 'react-icons/md';
import AppSelect from "./ui/AppSelect";

const API_URL = process.env.REACT_APP_SERVER_URL;

const getTierBadgeColor = (tier) => {
    switch (tier) {
        case 'platinum': return 'primary';
        case 'gold': return 'warning';
        case 'silver': return 'secondary';
        default: return 'info';
    }
};

/**
 * ThreePLPartnerDropdown - Reusable component for selecting 3PL partners
 *
 * Props:
 * - selectedPartnerId: Currently selected partner ID
 * - onSelectPartner: Callback when partner is selected (receives partnerId)
 * - disabled: Whether the dropdown is disabled
 * - showLabel: Whether to show the form label (default: true)
 * - dropoff: optional { lat, lng } of the delivery address
 *
 * WHY `dropoff` MATTERS
 * Partners define service areas, and the server has always been able to match a
 * drop off to the partners that cover it (GET /api/3pl-partners/service-areas).
 * Nothing called it. So this picker listed every active partner on the platform
 * and a vendor in Kampala could hand an order to a partner who only covers
 * Gulu, with nothing anywhere objecting. Passing `dropoff` makes the service
 * areas a partner enters actually decide what they get offered.
 *
 * Coverage SORTS and LABELS rather than filters. A vendor who knows an
 * arrangement the coverage map does not must still be able to pick, and a
 * partner who has simply not drawn their areas yet should not become
 * unbookable overnight. Uncovered partners drop to the bottom behind a divider
 * and say why.
 */
const ThreePLPartnerDropdown = ({
    selectedPartnerId,
    onSelectPartner,
    disabled = false,
    showLabel = true,
    dropoff = null
}) => {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [coveringIds, setCoveringIds] = useState(null);

    const lat = Number(dropoff?.lat);
    const lng = Number(dropoff?.lng);
    const hasDropoff = Number.isFinite(lat) && Number.isFinite(lng);

    useEffect(() => {
        fetchPartners();
    }, []);

    useEffect(() => {
        if (!hasDropoff) {
            setCoveringIds(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/3pl-partners/service-areas`, {
                    params: { lat, lng }
                });
                if (cancelled) return;
                const ids = (data?.data?.availablePartners || data?.availablePartners || [])
                    .map((p) => String(p._id));
                setCoveringIds(new Set(ids));
            } catch (_err) {
                // Coverage is an aid, not a gate. If the lookup fails the list
                // stays exactly as it was before this feature existed rather
                // than blocking the vendor from dispatching at all.
                if (!cancelled) setCoveringIds(null);
            }
        })();
        return () => { cancelled = true; };
    }, [hasDropoff, lat, lng]);

    const fetchPartners = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const response = await axios.get(`${API_URL}/api/3pl-partners`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            // console.log('3PL Partners API Response:', response.data);

            // API returns { success: true, data: { partners: [...] } }
            const partnersData = response.data?.data?.partners || response.data?.partners || [];

            // console.log('Partners data extracted:', partnersData);

            // Filter to show active partners first, but fall back to all partners if none are active
            const activePartners = Array.isArray(partnersData)
                ? partnersData.filter(partner => partner.status === 'active')
                : [];

            // If no active partners, show all partners
            const partnersToShow = activePartners.length > 0 ? activePartners : partnersData;

            // console.log('Partners to show:', partnersToShow);

            setPartners(Array.isArray(partnersToShow) ? partnersToShow : []);
            setError(null);
        } catch (err) {
            console.error('Error fetching 3PL partners:', err);
            setError('Failed to load 3PL partners');
            toast.error('Could not load 3PL partners');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Loading 3PL partners...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-danger">
                {error}
            </div>
        );
    }

    if (partners.length === 0) {
        return (
            <div className="text-muted">
                <small>No active 3PL partners available</small>
            </div>
        );
    }

    return (
        <Form.Group controlId="formPartnerSelect">
            {showLabel && (
                <Form.Label>
                    <MdLocalShipping style={{ marginRight: '5px' }} />
                    Select 3PL Partner
                </Form.Label>
            )}
            <AppSelect block
                id="formPartnerSelect"
                value={selectedPartnerId || ''}
                onChange={(e) => onSelectPartner(e.target.value)}
                disabled={disabled}
            >
                <option value="">-- Select 3PL Partner --</option>
                {(() => {
                    // `partnershipTier` is the model's field. Reading `tier` meant every
                    // option and badge fell through to the "Standard" default.
                    const tierOf = (p) => p.partnershipTier || p.tier || null;
                    const label = (p) => {
                        const t = tierOf(p);
                        return `${p.companyName}${t ? ` (${t.charAt(0).toUpperCase() + t.slice(1)})` : ""}`;
                    };
                    if (!coveringIds) {
                        return partners.map((p) => (
                            <option key={p._id} value={p._id}>{label(p)}</option>
                        ));
                    }
                    const covers = partners.filter((p) => coveringIds.has(String(p._id)));
                    const rest = partners.filter((p) => !coveringIds.has(String(p._id)));
                    return (
                        <>
                            {covers.map((p) => (
                                <option key={p._id} value={p._id}>{label(p)}</option>
                            ))}
                            {rest.length > 0 && (
                                <optgroup label="Does not cover this address">
                                    {rest.map((p) => (
                                        <option key={p._id} value={p._id}>{label(p)}</option>
                                    ))}
                                </optgroup>
                            )}
                        </>
                    );
                })()}
            </AppSelect>

            {coveringIds && (
                <small className="text-muted d-block mt-1">
                    {coveringIds.size > 0
                        ? `${coveringIds.size} partner${coveringIds.size === 1 ? "" : "s"} cover this delivery address.`
                        : "No partner has a service area covering this address. You can still pick one, but check they deliver there."}
                </small>
            )}

            {/* Show selected partner info */}
            {selectedPartnerId && (
                <div className="mt-2">
                    {partners
                        .filter(p => p._id === selectedPartnerId)
                        .map(partner => (
                            <div key={partner._id} style={{ fontSize: '12px' }}>
                                <Badge bg={getTierBadgeColor(partner.partnershipTier || partner.tier)} className="me-2">
                                    {partner.partnershipTier || partner.tier || 'Standard'}
                                </Badge>
                                <span className="text-muted">
                                    {partner.email} | {partner.phone || 'No phone'}
                                </span>
                            </div>
                        ))}
                </div>
            )}
        </Form.Group>
    );
};

export default ThreePLPartnerDropdown;
